import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, Copy, MonitorDown, RefreshCw } from "lucide-react";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import { Callout, Segmented } from "@/components/ui/Display.jsx";
import { SwitchRow } from "@/components/ui/Form.jsx";
import { useConfirm, useToast } from "@/components/ui/feedback.js";
import { useAuth } from "@/context/auth.js";
import { organizationService } from "@/lib/api.js";
import { ORGANIZATION_KEY, organizationQuery } from "@/lib/queries.js";
import { API_URL, apiErrorMessage } from "@/lib/apiClient.js";

/*
 * Download App — the one place an organization gets the install command.
 *
 * The command carries the org's own identifier (ORG=<id>), which the installer
 * pre-seeds so the desktop agent AUTO-REGISTERS to this organization on first
 * run — no typing on the target machine. Every signed-in org member can reach
 * it (admin/manager/employee); a superAdmin has no organization, so it just
 * says so.
 *
 * INSTALL_BASE is the backend origin: it serves install.sh / install.ps1 AND the
 * App Versions catalog the script pulls the latest build from — one origin for
 * the whole install flow. It tracks the SAME backend the frontend talks to
 * (API_URL / VITE_API_URL), so the command matches the environment automatically
 * — localhost in dev, the production API in prod. VITE_INSTALL_URL overrides it
 * for the rare case the installer host differs from the API host.
 */
const INSTALL_BASE = import.meta.env.VITE_INSTALL_URL || API_URL;

const PLATFORMS = [
    { value: "mac", label: "macOS" },
    { value: "win", label: "Windows" },
];

/*
 * Per-platform desktop auto-update opt-in (Organization.settings.autoUpdate{Win,Mac}).
 * Both default OFF — turning auto-update on is a deliberate per-OS choice, because
 * an unsigned macOS update resets Screen Recording / Input Monitoring and the user
 * must re-grant. The effective gate the backend serves is
 * (SuperAdmin master switch && this flag) — see backend combineAutoUpdate().
 * macOS carries a confirm() so the consequence is spelled out before opt-in.
 */
const AUTO_UPDATE_TOGGLES = [
    {
        key: "autoUpdateWin",
        label: "Windows — silent auto-update",
        hint: "Windows devices in this organization update themselves to the latest published build. Seamless — no permission prompts.",
        confirm: {
            title: "Turn on auto-update for Windows?",
            body: "Every Windows device in this organization will download and install each new published build on its own — you won't approve updates per device. If you want to check a build first, roll it out to one test machine before turning this on for the fleet.",
            okLabel: "Turn on Windows auto-update",
        },
    },
    {
        key: "autoUpdateMac",
        label: "macOS — silent auto-update",
        hint: "macOS devices update themselves too, but each update resets Screen Recording & Input Monitoring (the build is unsigned) and the app re-prompts — tracking pauses until the user re-grants.",
        confirm: {
            title: "Turn on auto-update for macOS?",
            body: "After every macOS update, macOS drops the Screen Recording and Input Monitoring permissions (the build is unsigned) and the app re-prompts for them. Until the person re-grants, that Mac stops capturing screenshots and keystrokes. Only turn this on if your macOS users can re-grant when asked.",
            okLabel: "Turn on macOS auto-update",
        },
    },
];

function commandFor(platform, org) {
    // One URL: the backend bakes its own origin into the served script's
    // API_BASE, so the command needs only ORG. (See app.js serveInstaller.)
    if (platform === "win") {
        return `$env:ORG="${org}"; irm ${INSTALL_BASE}/install.ps1 | iex`;
    }
    return `curl -fsSL ${INSTALL_BASE}/install.sh | ORG=${org} bash`;
}

const STEPS = {
    mac: [
        "Open Terminal on the Mac (Applications → Utilities → Terminal).",
        "Paste the command and press Return.",
        "Grant Screen Recording + Input Monitoring when macOS asks — then it tracks from the menu bar.",
    ],
    win: [
        "Open PowerShell on the PC (Start → type “PowerShell”).",
        "Paste the command and press Enter.",
        "It installs, auto-registers, and starts tracking from the system tray.",
    ],
};

function defaultPlatform() {
    if (typeof navigator !== "undefined" && /win/i.test(navigator.userAgent)) return "win";
    return "mac";
}

export default function DownloadApp() {
    const { user } = useAuth();
    const toast = useToast();
    const confirm = useConfirm();
    const queryClient = useQueryClient();

    const org = user?.organization?.identifier || "";
    const orgId =
        typeof user?.organization === "object" ? user?.organization?._id : user?.organization;
    // Auto-update is an org-wide choice — only admins may change it, and the
    // updateOrganization endpoint is admin-only, so the card is admin-only too.
    const isAdmin = user?.role === "admin";

    const [platform, setPlatform] = useState(defaultPlatform);
    const [copied, setCopied] = useState(false);

    const cmd = commandFor(platform, org);

    // Only admins can read/write org settings; skip the fetch otherwise.
    const orgQuery = useQuery({ ...organizationQuery(orgId), enabled: isAdmin && !!orgId });
    const settings = orgQuery.data?.organization?.settings ?? {};

    const save = useMutation({
        mutationFn: (patch) => organizationService.updateOrganization(orgId, patch),
        onSuccess: (_res, patch) => {
            const [key, value] = Object.entries(patch.settings)[0];
            const os = key === "autoUpdateMac" ? "macOS" : "Windows";
            toast(`${os} auto-update ${value ? "on" : "off"}`, "success");
            queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEY(orgId) });
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not save the setting."), "error"),
    });

    // Turning a platform ON may confirm first (macOS spells out the permission
    // reset); turning OFF never asks.
    const setAuto = async (field, next) => {
        if (next && field.confirm) {
            const ok = await confirm({ tone: "warning", ...field.confirm });
            if (!ok) return;
        }
        save.mutate({ settings: { [field.key]: next } });
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(cmd);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard blocked (insecure context) — the user can still select + copy */
        }
    };

    return (
        <>
            <PageChrome breadcrumb={["Download App"]} title="Download App" />

            <main className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-4 lg:p-5">
                <div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Download App</h1>
                    <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                        Install the desktop tracker on any computer. The command below already carries
                        your organization code, so the app registers to your organization automatically.
                    </p>
                </div>

                {!org ? (
                    <Card>
                        <CardBody>
                            <p className="text-[13px] text-slate-500 dark:text-slate-400">
                                Your account is not attached to an organization, so there is no install
                                code to show here.
                            </p>
                        </CardBody>
                    </Card>
                ) : (
                    <Card>
                        <CardHead className="flex-wrap gap-3">
                            <CardTitle icon={<MonitorDown className="size-4.5" />}>
                                Install command
                            </CardTitle>
                            <Segmented value={platform} onChange={setPlatform} options={PLATFORMS} />
                        </CardHead>
                        <CardBody className="flex flex-col gap-4">
                            <div className="flex flex-wrap items-center gap-2 text-[13px]">
                                <Building2 className="size-4 text-slate-400" />
                                <span className="text-slate-500 dark:text-slate-400">
                                    Organization code:
                                </span>
                                <code className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[12.5px] font-semibold text-slate-800 dark:bg-slate-800 dark:text-slate-100">
                                    {org}
                                </code>
                            </div>

                            <div className="relative">
                                <pre className="overflow-x-auto rounded-lg bg-slate-900 py-4 pr-28 pl-4 font-mono text-[13px] leading-relaxed text-slate-100">
                                    {cmd}
                                </pre>
                                <Button
                                    variant="default"
                                    className="absolute top-2.5 right-2.5"
                                    onClick={copy}
                                >
                                    {copied ? (
                                        <>
                                            <Check className="size-4" /> Copied
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="size-4" /> Copy
                                        </>
                                    )}
                                </Button>
                            </div>

                            <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-[13px] leading-relaxed text-slate-600 dark:text-slate-300">
                                {STEPS[platform].map((step, i) => (
                                    <li key={i}>{step}</li>
                                ))}
                            </ol>
                        </CardBody>
                    </Card>
                )}

                {/* Auto-update — org-wide, per platform. Admin-only control. */}
                {isAdmin && org && (
                    <Card>
                        <CardHead>
                            <CardTitle icon={<RefreshCw className="size-4.5" />}>
                                Auto-update
                            </CardTitle>
                        </CardHead>
                        <CardBody className="flex flex-col gap-4">
                            <Callout tone="info">
                                <p className="text-[12.5px] leading-relaxed">
                                    When on, this organization's installed apps update themselves to
                                    the latest published build — no reinstall. Off by default; turn on
                                    each platform only when you're ready. The platform administrator
                                    can also pause auto-update for everyone, in which case these stay
                                    off regardless.
                                </p>
                            </Callout>
                            <div className="space-y-2.5">
                                {AUTO_UPDATE_TOGGLES.map((field) => (
                                    <SwitchRow
                                        key={field.key}
                                        label={field.label}
                                        hint={field.hint}
                                        checked={!!settings[field.key]}
                                        disabled={orgQuery.isLoading || save.isPending}
                                        onChange={(e) => setAuto(field, e.target.checked)}
                                    />
                                ))}
                            </div>
                        </CardBody>
                    </Card>
                )}
            </main>
        </>
    );
}
